import { anchorInstantForCivilDayInTimeZone, parseDateOnly } from './dateOnly';

export interface FormatDateOnlyForDisplayOptions {
  locale?: string;
  format?: 'short' | 'long';
  /** @default 'timezone-anchored' — web behavior */
  displayProfile?: 'timezone-anchored' | 'civil-utc';
  /** Required when displayProfile is 'timezone-anchored' (web). Ignored for civil-utc (portal). */
  timeZone?: string;
}

function formatDateOnlyAnchorWithIntl(
  dateOnly: string,
  options: {
    locale: string;
    timeZone: string;
    format: 'short' | 'long';
    shortMonthStyle: 'numeric' | 'short';
  }
): string | null {
  const parsed = parseDateOnly(dateOnly);
  if (parsed === null) return null;

  const [ys, ms, ds] = parsed.split('-');
  const y = Number(ys);
  const mo = Number(ms);
  const d = Number(ds);
  const anchorMs = Date.UTC(y, mo - 1, d, 12, 0, 0);
  const anchor = new Date(anchorMs);
  if (isNaN(anchor.getTime())) return null;

  const monthStyle = options.format === 'long' ? 'long' : options.shortMonthStyle;

  try {
    return new Intl.DateTimeFormat(options.locale, {
      timeZone: options.timeZone,
      year: 'numeric',
      month: monthStyle,
      day: 'numeric',
    }).format(anchor);
  } catch {
    return null;
  }
}

/**
 * Formats a validated date-only string for display.
 * Use displayProfile to preserve web (timezone-anchored) vs portal (civil-utc) semantics.
 */
export function formatDateOnlyForDisplay(
  dateOnly: string,
  options: FormatDateOnlyForDisplayOptions
): string | null {
  const locale = options.locale ?? 'en-US';
  const format = options.format ?? 'short';
  const profile = options.displayProfile ?? 'timezone-anchored';

  if (profile === 'civil-utc') {
    return formatDateOnlyAnchorWithIntl(dateOnly, {
      locale,
      timeZone: 'UTC',
      format,
      shortMonthStyle: 'short',
    });
  }

  const { timeZone } = options;
  if (typeof timeZone !== 'string' || timeZone.length === 0) {
    return null;
  }

  return formatDateOnlyAnchorWithIntl(dateOnly, {
    locale,
    timeZone,
    format,
    shortMonthStyle: 'numeric',
  });
}

export function formatSchedulerPickerDayHeader(
  dateOnly: string,
  iana: string,
  locale: string
): string | null {
  const anchor = anchorInstantForCivilDayInTimeZone(dateOnly, iana);
  if (!anchor) {
    return null;
  }

  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone: iana,
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(anchor);
  } catch {
    return null;
  }
}
